<template>
  <div class="ai-chat-container">
    <!-- AI对话按钮 -->
    <button 
      class="ai-chat-button" 
      :class="{ dragging: isDragging }"
      :style="buttonStyle"
      @mousedown="handleStart"
      @touchstart="handleStart"
      @click="handleClick"
      title="AI助手"
    >
      <i class="fas fa-robot"></i>
    </button>

    <!-- AI对话弹窗 -->
    <div 
      class="ai-chat-modal" 
      :class="{ active: isChatVisible }"
      v-show="isChatVisible"
    >
      <div class="ai-chat-window">
        <!-- 对话窗口头部 -->
        <div class="ai-chat-header">
          <div class="ai-chat-title">
            <i class="fas fa-robot"></i>
            <span>非遗索思AI助手</span>
          </div>
          <button class="ai-chat-close" @click="closeChat" title="关闭">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- 对话消息区域 -->
        <div class="ai-chat-messages" ref="messagesContainer">
          <div 
            v-for="message in messages" 
            :key="message.id"
            class="chat-message"
            :class="message.sender"
          >
            <div class="message-avatar" :class="message.sender">
              <i v-if="message.sender === 'ai'" class="fas fa-robot"></i>
              <i v-else class="fas fa-user"></i>
            </div>
            <div class="message-content">
              <div class="message-text" v-html="formatMessage(message.content)"></div>
              <div class="message-time">{{ formatTime(message.timestamp) }}</div>
            </div>
          </div>
          
          <!-- 打字指示器 -->
          <div v-if="isTyping" class="chat-message ai typing-indicator">
            <div class="message-avatar ai">
              <i class="fas fa-robot"></i>
            </div>
            <div class="message-content">
              <div class="typing-dots">
                <span class="typing-dot"></span>
                <span class="typing-dot"></span>
                <span class="typing-dot"></span>
              </div>
            </div>
          </div>
        </div>

        <!-- 输入区域 -->
        <div class="ai-chat-input-area">
          <textarea 
            class="ai-chat-input" 
            v-model="inputMessage"
            @keydown.enter.prevent="handleEnterKey"
            placeholder="请输入您想了解的非遗问题..."
            rows="1"
            ref="chatInput"
          ></textarea>
          <button 
            class="ai-chat-send" 
            @click="sendMessage"
            :disabled="!inputMessage.trim() || isTyping"
            title="发送"
          >
            <i class="fas fa-paper-plane"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'AiChatComponent',
  
  data() {
    return {
      inputMessage: '',
      // 拖拽相关状态
      dragStartTime: 0,
      hasMoved: false,
      startMouseX: 0,
      startMouseY: 0,
      startButtonX: 0,
      startButtonY: 0,
      // 按钮配置
      BUTTON_SIZE: 60,
      EDGE_MARGIN: 30,
      TOP_MARGIN: 100
    }
  },
  
  computed: {
    ...mapGetters('aiChat', [
      'isChatVisible',
      'getMessages',
      'isTyping',
      'getButtonPosition',
      'isDragging'
    ]),
    
    /**
     * 消息列表
     */
    messages() {
      return this.getMessages
    },
    
    /**
     * 按钮样式
     */
    buttonStyle() {
      const pos = this.getButtonPosition
      return {
        left: pos.x + 'px',
        top: pos.y + 'px',
        transform: this.isDragging ? 'scale(1.05)' : 'scale(1)'
      }
    }
  },
  
  mounted() {
    this.initButtonPosition()
    this.initEventListeners()
  },
  
  beforeDestroy() {
    this.removeEventListeners()
  },
  
  methods: {
    ...mapActions('aiChat', [
      'openChat',
      'closeChat',
      'sendMessage',
      'updateButtonPosition',
      'setDraggingStatus'
    ]),
    
    /**
     * 处理拖拽开始
     * @param {Event} e - 事件对象
     */
    handleStart(e) {
      this.dragStartTime = Date.now()
      this.hasMoved = false
      this.setDraggingStatus(true)
      
      // 获取鼠标/触摸位置
      if (e.type.includes('touch')) {
        this.startMouseX = e.touches[0].clientX
        this.startMouseY = e.touches[0].clientY
      } else {
        this.startMouseX = e.clientX
        this.startMouseY = e.clientY
      }
      
      // 获取按钮当前位置
      const currentPos = this.getCurrentPosition()
      this.startButtonX = currentPos.x
      this.startButtonY = currentPos.y
      
      e.preventDefault()
      
      // 添加移动和结束事件监听
      if (e.type.includes('touch')) {
        document.addEventListener('touchmove', this.handleMove, { passive: false })
        document.addEventListener('touchend', this.handleEnd)
      } else {
        document.addEventListener('mousemove', this.handleMove)
        document.addEventListener('mouseup', this.handleEnd)
      }
    },
    
    /**
     * 处理拖拽移动
     * @param {Event} e - 事件对象
     */
    handleMove(e) {
      if (!this.isDragging) return
      
      e.preventDefault()
      
      // 获取当前鼠标/触摸位置
      let currentMouseX, currentMouseY
      if (e.type.includes('touch')) {
        currentMouseX = e.touches[0].clientX
        currentMouseY = e.touches[0].clientY
      } else {
        currentMouseX = e.clientX
        currentMouseY = e.clientY
      }
      
      // 计算移动距离
      const deltaX = currentMouseX - this.startMouseX
      const deltaY = currentMouseY - this.startMouseY
      
      // 检查是否真的移动了
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)
      if (distance > 3) {
        this.hasMoved = true
      }
      
      // 计算新位置
      const newX = this.startButtonX + deltaX
      const newY = this.startButtonY + deltaY
      
      this.setButtonPosition(newX, newY)
    },
    
    /**
     * 处理拖拽结束
     */
    handleEnd() {
      if (!this.isDragging) return
      
      const dragDuration = Date.now() - this.dragStartTime
      this.setDraggingStatus(false)
      
      // 移除事件监听
      document.removeEventListener('mousemove', this.handleMove)
      document.removeEventListener('mouseup', this.handleEnd)
      document.removeEventListener('touchmove', this.handleMove)
      document.removeEventListener('touchend', this.handleEnd)
      
      // 如果没有移动且时间很短，认为是点击
      if (!this.hasMoved && dragDuration < 300) {
        this.openChat()
      } else if (this.hasMoved) {
        // 拖拽后吸附到边缘
        this.snapToEdge()
      }
    },
    
    /**
     * 处理点击事件
     */
    handleClick(e) {
      // 如果正在拖拽，阻止点击
      if (this.isDragging || this.hasMoved) {
        e.preventDefault()
        e.stopPropagation()
      }
    },
    
    /**
     * 发送消息
     */
    async handleSendMessage() {
      if (!this.inputMessage.trim() || this.isTyping) return
      
      const message = this.inputMessage.trim()
      this.inputMessage = ''
      
      // 调用store中的发送消息action
      await this.$store.dispatch('aiChat/sendMessage', message)
      
      // 滚动到底部
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },
    
    /**
     * 处理回车键
     * @param {Event} e - 键盘事件
     */
    handleEnterKey(e) {
      if (!e.shiftKey) {
        this.handleSendMessage()
      }
    },
    
    /**
     * 格式化消息内容
     * @param {string} content - 消息内容
     * @returns {string} 格式化后的内容
     */
    formatMessage(content) {
      return content.replace(/\n/g, '<br>')
    },
    
    /**
     * 格式化时间
     * @param {number} timestamp - 时间戳
     * @returns {string} 格式化后的时间
     */
    formatTime(timestamp) {
      const date = new Date(timestamp)
      return date.toLocaleTimeString('zh-CN', { 
        hour: '2-digit', 
        minute: '2-digit' 
      })
    },
    
    /**
     * 滚动到底部
     */
    scrollToBottom() {
      if (this.$refs.messagesContainer) {
        this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight
      }
    },
    
    /**
     * 设置按钮位置
     * @param {number} x - X坐标
     * @param {number} y - Y坐标
     */
    setButtonPosition(x, y) {
      // 限制在屏幕范围内
      const maxX = window.innerWidth - this.BUTTON_SIZE - this.EDGE_MARGIN
      const maxY = window.innerHeight - this.BUTTON_SIZE - this.EDGE_MARGIN
      
      const finalX = Math.max(this.EDGE_MARGIN, Math.min(maxX, x))
      const finalY = Math.max(this.TOP_MARGIN, Math.min(maxY, y))
      
      this.updateButtonPosition({ x: finalX, y: finalY })
    },
    
    /**
     * 获取按钮当前位置
     * @returns {Object} 当前位置
     */
    getCurrentPosition() {
      return this.getButtonPosition
    },
    
    /**
     * 吸附到边缘
     */
    snapToEdge() {
      const currentPos = this.getCurrentPosition()
      const windowWidth = window.innerWidth
      const centerX = windowWidth / 2
      
      let newX
      if (currentPos.x < centerX) {
        // 吸附到左边
        newX = this.EDGE_MARGIN
      } else {
        // 吸附到右边
        newX = windowWidth - this.BUTTON_SIZE - this.EDGE_MARGIN
      }
      
      this.updateButtonPosition({ x: newX, y: currentPos.y })
    },
    
    /**
     * 初始化按钮位置
     */
    initButtonPosition() {
      const initialX = window.innerWidth - this.BUTTON_SIZE - this.EDGE_MARGIN
      const initialY = window.innerHeight - 150
      this.updateButtonPosition({ x: initialX, y: initialY })
    },
    
    /**
     * 初始化事件监听
     */
    initEventListeners() {
      // 窗口大小改变时重新计算位置
      this.handleResize = () => {
        const currentPos = this.getCurrentPosition()
        const maxX = window.innerWidth - this.BUTTON_SIZE - this.EDGE_MARGIN
        const maxY = window.innerHeight - this.BUTTON_SIZE - this.EDGE_MARGIN
        
        const newX = Math.min(currentPos.x, maxX)
        const newY = Math.min(currentPos.y, maxY)
        
        this.updateButtonPosition({ x: newX, y: newY })
      }
      
      window.addEventListener('resize', this.handleResize)
    },
    
    /**
     * 移除事件监听
     */
    removeEventListeners() {
      window.removeEventListener('resize', this.handleResize)
    }
  },
  
  watch: {
    /**
     * 监听消息变化，自动滚动到底部
     */
    messages: {
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      },
      deep: true
    }
  }
}
</script>

<style scoped>
/* 组件特定样式，其他样式通过全局CSS文件加载 */
.ai-chat-container {
  position: relative;
  z-index: 9999;
}

.ai-chat-button {
  position: fixed;
  transition: transform 0.3s ease;
  z-index: 9999;
}

.message-text {
  word-wrap: break-word;
  white-space: pre-wrap;
}

.message-time {
  font-size: 0.8em;
  color: #999;
  margin-top: 4px;
}
</style> 